import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Gem, Bomb } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GameFrame, BetControls } from "@/components/GameFrame";
import { useAudio } from "@/context/GameFeel";

export default function MinesGame() {
  const { user, setUser } = useAuth();
  const { react, sfx } = useAudio();
  const [bet, setBet] = useState(100);
  const [mines, setMines] = useState(3);
  const [round, setRound] = useState(null); // {round_id}
  const [revealed, setRevealed] = useState({}); // tile -> 'gem'
  const [bombs, setBombs] = useState([]);
  const [mult, setMult] = useState(1);
  const [potential, setPotential] = useState(0);
  const [over, setOver] = useState(false);

  const start = async () => {
    if (bet > (user?.coins ?? 0)) return toast.error("Not enough coins");
    try {
      const { data } = await api.post("/games/mines/start", { bet, mines });
      setRound(data);
      setRevealed({});
      setBombs([]);
      setMult(1);
      setPotential(0);
      setOver(false);
      setUser((u) => ({ ...u, coins: data.coins }));
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const reveal = async (tile) => {
    if (!round || over || revealed[tile] != null) return;
    try {
      const { data } = await api.post("/games/mines/reveal", { round_id: round.round_id, tile });
      if (data.busted) {
        setBombs(data.mine_positions);
        setOver(true);
        setUser((u) => ({ ...u, coins: data.coins }));
        toast.error("💥 Boom! You hit a mine.");
        react({ outcome: "loss", game: "mines" });
        setTimeout(() => setRound(null), 1800);
      } else {
        sfx("correct");
        setRevealed((r) => ({ ...r, [tile]: "gem" }));
        setMult(data.multiplier);
        setPotential(data.potential_payout);
      }
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const cashout = async () => {
    if (!round || over) return;
    try {
      const { data } = await api.post("/games/mines/cashout", { round_id: round.round_id });
      setBombs(data.mine_positions);
      setOver(true);
      setUser((u) => ({ ...u, coins: data.coins }));
      toast.success(`Locked in ${data.multiplier}x · +${data.payout.toLocaleString()} coins`);
      react({ outcome: "win", game: "mines", big: data.multiplier >= 5 });
      setTimeout(() => setRound(null), 1800);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const active = round && !over;

  return (
    <GameFrame title="Mines" subtitle="Reveal gems, avoid the bombs"
      side={<>
        {active ? (
          <button data-testid="mines-cashout-button" onClick={cashout} disabled={Object.keys(revealed).length === 0}
            className="w-full py-5 rounded-2xl bg-gradient-to-r from-emerald-500 to-cyan-600 font-black text-lg hover:opacity-90 transition-opacity duration-200 disabled:opacity-40">
            LOCK IN · {potential.toLocaleString()} coins ({mult}x)
          </button>
        ) : (
          <div className="glass rounded-2xl p-5 border border-white/10">
            <label className="text-sm text-gray-400 mb-2 block">Mines: {mines}</label>
            <input type="range" min={1} max={24} value={mines} onChange={(e) => setMines(parseInt(e.target.value))} className="w-full mb-4 accent-amber-500" data-testid="mines-count-slider" />
            <BetControls bet={bet} setBet={setBet} coins={user?.coins} onPlay={start} playLabel="START GAME" testid="mines-start" />
          </div>
        )}
        <div className="glass rounded-2xl p-5 border border-white/10 text-sm text-gray-400">
          Each safe gem raises your multiplier. Lock in before hitting a mine. More mines = bigger multipliers. Coins only.
        </div>
      </>}>
      <div className="glass rounded-3xl p-6 border border-amber-500/20 glow-coins">
        <div className="grid grid-cols-5 gap-2.5 max-w-md mx-auto">
          {Array.from({ length: 25 }).map((_, tile) => {
            const isGem = revealed[tile] === "gem";
            const isBomb = bombs.includes(tile);
            return (
              <motion.button key={tile} data-testid={`mines-tile-${tile}`} onClick={() => reveal(tile)} disabled={!active}
                whileTap={{ scale: 0.92 }}
                className={`aspect-square rounded-xl flex items-center justify-center text-2xl transition-colors duration-200 border
                  ${isGem ? "bg-emerald-500/25 border-emerald-500/50" : isBomb ? "bg-red-500/25 border-red-500/50" : "bg-black/40 border-white/10 hover:bg-white/5"}`}>
                {isGem && <Gem className="w-6 h-6 text-emerald-400" />}
                {isBomb && !isGem && <Bomb className="w-6 h-6 text-red-400" />}
              </motion.button>
            );
          })}
        </div>
      </div>
    </GameFrame>
  );
}
